import React, { useState, useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./BookingModal.css";
import BookingCalendar from "./BookingCalendar";
import { toISODate, fromISODate, formatSlotLabel } from "../../utils/dates";
import { resourcesForService } from "../../utils/categoryTeam";
import { serviceMinutes } from "../../utils/serviceDuration";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  EMAIL_MAX_LENGTH,
  EMAIL_PATTERN,
  PHONE_MAX_LENGTH,
  PHONE_PATTERN,
  isValidEmail,
  isValidPhone,
} from "../../utils/contactFormat";
import {
  GetPublicServicesAction,
  GetPublicResourcesAction,
  GetAvailableTimeSlotsAction,
  GetAvailableDatesAction,
  CreatePublicBookingAction,
} from "../../store/action";

/**
 * How many days ahead one lookup scans for free days. One answer covers
 * several pages of the strip; each page asks again from where it starts, so
 * this bounds a single request, not how far a visitor can go.
 */
const DATE_WINDOW_DAYS = 60;

/** `iso` ("yyyy-MM-dd") moved by `days`, on the local calendar. */
const addDaysISO = (iso, days) => {
  const date = fromISODate(iso);
  date.setDate(date.getDate() + days);
  return toISODate(date);
};

/**
 * Whether the account details on file would be refused if typed into the form
 * — an old profile with "177" for a phone. Those open as fields to correct, not
 * as a summary the booking then fails on. No phone at all is fine: the summary
 * says so and the booking goes without one, as it always has.
 */
const detailsNeedFixing = (customer) =>
  !isValidEmail(customer.email) || Boolean(customer.phone && !isValidPhone(customer.phone));

/**
 * Booking Modal Component
 * Allows users to book appointments by selecting a resource, date, and time slot
 *
 * Used from two places, and the props are what tell them apart:
 *
 * - The **public booking page** (`/:organizationSlug`), where the visitor may be
 *   anyone.
 * - The signed-in **Book tab**, where `prefillCustomer` is set and the modal
 *   opens over the search results.
 *
 * `initialSelection` lets either page open this already filled in, which is how a
 * slot picked in a search survives the jump: someone who chose "Thursday 10:00"
 * there must not be made to choose it again here.
 *
 * `prefillCustomer` is the answer to the same complaint one layer down — a
 * signed-in customer should not be asked to type the name, email and phone their
 * account already holds. The details stay correctable (a booking for a different
 * phone number is a real need), but they start collapsed behind a summary rather
 * than as three empty required fields.
 *
 * The booking always goes through the anonymous public endpoint, signed in or
 * not. That endpoint is the one that resolves availability and stages the
 * notification outbox, and there is deliberately only one way to create a
 * booking from the customer side.
 *
 * `embedded` is the third place: the widget on a business's own site
 * (`/embed/:slug`). The same form, without the overlay — the iframe is the
 * frame. Inline there is nothing to close, so the close controls go and "Done"
 * starts a fresh booking instead; in the popup, closing is the host page's job
 * and `onClose` asks it to.
 */
const BookingModal = ({
  isOpen,
  onClose,
  organizationSlug,
  organizationName,
  initialSelection,
  prefillCustomer,
  embedded,
  source,
  accentStyle,
  onBooked,
}) => {
  const { t, i18n } = useTranslation();
  const dateLabelId = useId();
  const calendarId = useId();
  const calendarToggleRef = useRef(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState("");
  const [selectedResource, setSelectedResource] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  /**
   * Where each page of the date strip starts ("yyyy-MM-dd"), newest last;
   * empty is the first page, which starts today. A stack rather than a week
   * offset because a page holds the next free days, not a calendar week, so
   * where the next one starts depends on what this one showed.
   */
  const [datePageStarts, setDatePageStarts] = useState([]);
  /** Whether the month view stands in for the strip. */
  const [calendarOpen, setCalendarOpen] = useState(false);

  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  /**
   * The free days from the current page's start on, as "yyyy-MM-dd". Null
   * until known, and again when the lookup fails — then consecutive days are
   * offered, as before, and picking one still asks the server for its times.
   */
  const [availableDates, setAvailableDates] = useState(null);
  const [datesLoading, setDatesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  /** The confirmed booking, which replaces the form rather than an alert(). */
  const [confirmed, setConfirmed] = useState(null);

  const [customerInfo, setCustomerInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    notes: "",
  });

  /**
   * Whether the identity fields are showing. Starts hidden when we already know
   * who this is, and once opened stays open — collapsing the fields back under
   * someone who is mid-correction would throw away the keystroke they are on.
   */
  const [editingDetails, setEditingDetails] = useState(
    !prefillCustomer || detailsNeedFixing(prefillCustomer),
  );

  /**
   * Seven days to a page, five on a narrow screen: the widget may sit in a
   * phone-width iframe, where seven cards are too thin to read.
   */
  const isNarrow = useMediaQuery("(max-width: 520px)");
  const daysPerPage = isNarrow ? 5 : 7;

  // Local calendar, not UTC — see toISODate. Through toISOString the strip
  // rolled forward a day once the local clock passed the UTC offset, so an
  // evening visitor booked the day after the one they tapped.
  const todayValue = toISODate(new Date());
  const datePageStart = datePageStarts[datePageStarts.length - 1] ?? todayValue;

  // Fetch services when modal opens
  useEffect(() => {
    if (isOpen && organizationSlug) {
      fetchServices();
    }
  }, [isOpen, organizationSlug]);

  // Adopt a selection made elsewhere — a slot clicked in a search — and the
  // account details of whoever is signed in.
  //
  // Only on open, and only for fields they actually carry: after that the
  // visitor owns the form, and re-applying would fight them for control of the
  // very fields they are editing. The service and resource ids become strings
  // because the selects are keyed by string value.
  useEffect(() => {
    if (!isOpen) return;

    if (initialSelection) {
      const { serviceId, resourceId, date, timeSlot } = initialSelection;
      if (serviceId) setSelectedService(String(serviceId));
      if (resourceId) setSelectedResource(String(resourceId));
      if (date) setSelectedDate(date);
      if (timeSlot) setSelectedTimeSlot(timeSlot);
    }

    if (prefillCustomer) {
      setCustomerInfo((prev) => ({
        ...prev,
        fullName: prefillCustomer.fullName || "",
        email: prefillCustomer.email || "",
        phone: prefillCustomer.phone || "",
      }));
      setEditingDetails(detailsNeedFixing(prefillCustomer));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fetch resources when service is selected
  useEffect(() => {
    if (selectedService && organizationSlug) {
      fetchResources();
    }
  }, [selectedService, organizationSlug]);

  // The free days from where the strip's page starts, so a closed day or a
  // fully booked one is never offered and then answered with "no times". The
  // server decides, through the same rule as the times themselves: the public
  // resource carries no schedule to judge by.
  useEffect(() => {
    if (!selectedResource || !selectedService || !organizationSlug) {
      setAvailableDates(null);
      setDatesLoading(false);
      return undefined;
    }

    // A quick click through the pages must not let an older answer land last.
    let stale = false;

    const fetchAvailableDates = async () => {
      setDatesLoading(true);
      setAvailableDates(null);
      try {
        const dates = await GetAvailableDatesAction(
          organizationSlug,
          selectedResource,
          selectedService,
          datePageStart,
          DATE_WINDOW_DAYS,
        );
        if (!stale) setAvailableDates(Array.isArray(dates) ? dates : null);
      } catch (err) {
        console.error("Error fetching available dates:", err);
        if (!stale) setAvailableDates(null);
      } finally {
        if (!stale) setDatesLoading(false);
      }
    };

    fetchAvailableDates();

    return () => {
      stale = true;
    };
  }, [selectedResource, selectedService, organizationSlug, datePageStart]);

  // Fetch time slots when resource and date are selected
  useEffect(() => {
    if (selectedResource && selectedDate && selectedService) {
      fetchTimeSlots();
    }
  }, [selectedResource, selectedDate, selectedService]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await GetPublicServicesAction(organizationSlug);
      setServices(data);
      // A discovery result already chose a service; show its category too.
      const initialService = data.find((service) => String(service.id) === String(initialSelection?.serviceId));
      if (initialService) setSelectedCategory(initialService.categoryId ?? "other");
    } catch (err) {
      console.error("Error fetching services:", err);
      setError({ key: "Booking.errors.services" });
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await GetPublicResourcesAction(organizationSlug);
      setResources(data);
    } catch (err) {
      console.error("Error fetching resources:", err);
      setError({ key: "Booking.errors.staff" });
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await GetAvailableTimeSlotsAction(
        organizationSlug,
        selectedResource,
        selectedService,
        selectedDate
      );
      setTimeSlots(data);
      // A time picked on the previous day survives only if this day has it
      // too. Kept regardless, it went unhighlighted yet still enabled
      // "Confirm", and the booking was refused for a time nobody could see.
      setSelectedTimeSlot((current) =>
        current && Array.isArray(data) && data.includes(current) ? current : "",
      );
    } catch (err) {
      console.error("Error fetching time slots:", err);
      setError({ key: "Booking.errors.slots" });
      setTimeSlots([]);
    } finally {
      setLoading(false);
    }
  };

  // The strip: the next free days one after another, so a closed weekend or a
  // full day leaves no hole in it. Without an answer from the server it falls
  // back to consecutive days, as it was before.
  const stripDates = availableDates
    ? availableDates.slice(0, daysPerPage)
    : Array.from({ length: daysPerPage }, (_, i) => addDaysISO(datePageStart, i));
  const hasMoreDates = availableDates ? availableDates.length > daysPerPage : true;

  const handlePreviousDates = () => {
    if (datePageStarts.length === 0) return;
    setDatePageStarts((starts) => starts.slice(0, -1));
    setSelectedDate("");
    setSelectedTimeSlot("");
  };

  const handleNextDates = () => {
    if (!hasMoreDates || stripDates.length === 0) return;
    const nextStart = addDaysISO(stripDates[stripDates.length - 1], 1);
    setDatePageStarts((starts) => [...starts, nextStart]);
    setSelectedDate("");
    setSelectedTimeSlot("");
  };

  /** Back to the strip, with focus on the button that left it. */
  const closeCalendar = () => {
    setCalendarOpen(false);
    calendarToggleRef.current?.focus();
  };

  // A day picked in the month view goes back to the strip, which must then
  // show it: left where it was, the strip would hide the very day just chosen.
  // A day already on show leaves the strip alone; any other becomes the start
  // of a page of its own, and "back" from there returns to today.
  const handleCalendarSelect = (dateValue) => {
    setSelectedDate(dateValue);
    if (!stripDates.includes(dateValue)) {
      setDatePageStarts(dateValue === todayValue ? [] : [dateValue]);
    }
    closeCalendar();
  };

  // The language the page is in, not the browser's: a Romanian page showed
  // "WED / Oct 7" beside "ASTĂZI".
  const dateLocale = i18n?.resolvedLanguage || i18n?.language;

  /** One part of a date, without the dot Romanian abbreviations end in ("mie." → "mie"). */
  const datePart = (date, options) =>
    date.toLocaleDateString(dateLocale, options).replace(/\.$/, "");

  const handleCustomerInfoChange = (field, value) => {
    setCustomerInfo((prev) => ({ ...prev, [field]: value }));
  };

  // Categories are derived from the services themselves, so businesses that
  // don't use them (no service has a category) see no category UI at all.
  const categories = services.reduce((acc, service) => {
    if (
      service.categoryId != null &&
      !acc.some((c) => c.id === service.categoryId)
    ) {
      acc.push({ id: service.categoryId, name: service.categoryName });
    }
    return acc;
  }, []);
  categories.sort((a, b) => a.name.localeCompare(b.name));

  const hasUncategorized = services.some((s) => s.categoryId == null);
  const showCategories = categories.length > 0;

  const needsCategory = showCategories && selectedCategory == null;
  const visibleServices =
    !showCategories
      ? services
      : needsCategory ? [] : selectedCategory === "other"
        ? services.filter((s) => s.categoryId == null)
        : services.filter((s) => s.categoryId === selectedCategory);

  // Only the people who handle the chosen service's category. A category with
  // no team leaves everyone on offer, which is every business that never set
  // one up.
  const offeredResources = resourcesForService(
    resources,
    services.find((s) => String(s.id) === String(selectedService)),
  );

  const handleCategorySelect = (categoryKey) => {
    setSelectedCategory(categoryKey);
    // A different category means a different service list; clear the chain.
    setSelectedService("");
    setSelectedResource("");
    setSelectedDate("");
    setSelectedTimeSlot("");
    setDatePageStarts([]);
  };

  const resetForm = () => {
    setSelectedCategory(null);
    setSelectedService("");
    setSelectedResource("");
    setSelectedDate("");
    setSelectedTimeSlot("");
    setDatePageStarts([]);
    setCalendarOpen(false);
    setConfirmed(null);
    setError(null);
    // Back to whatever the account holds, not to blank: closing a confirmation
    // and booking again should not re-ask for details we know.
    setCustomerInfo({
      fullName: prefillCustomer?.fullName || "",
      email: prefillCustomer?.email || "",
      phone: prefillCustomer?.phone || "",
      notes: "",
    });
    setEditingDetails(!prefillCustomer || detailsNeedFixing(prefillCustomer));
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  /** Inline, nothing surrounds the form that could be closed. */
  const canClose = Boolean(onClose);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (needsCategory) return;

    try {
      setSubmitting(true);
      setError(null);

      const bookingData = {
        organizationSlug,
        serviceId: parseInt(selectedService),
        resourceId: parseInt(selectedResource),
        bookingDate: selectedDate,
        timeSlot: selectedTimeSlot,
        customerName: customerInfo.fullName,
        customerEmail: customerInfo.email.trim(),
        customerPhone: customerInfo.phone.trim(),
        notes: customerInfo.notes || null,
        // Only the widget says where it is; the server treats silence as the
        // booking page.
        ...(source ? { source } : {}),
      };

      const booking = await CreatePublicBookingAction(bookingData);

      // An inline confirmation, not an alert(): this modal now opens inside the
      // dashboard, where a native dialog is jarring, and the confirmation code
      // is worth being able to read and copy rather than dismiss.
      setConfirmed(booking ?? {});
      onBooked?.();
    } catch (err) {
      console.error("Error creating booking:", err);
      const message = err.response?.data?.message;
      setError(message ? { message } : { key: "Booking.errors.create" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const errorText = error ? (error.message ?? t(error.key)) : null;

  const contentClassName = [
    "booking-modal-content",
    embedded ? "booking-embedded" : "",
    embedded === "inline" ? "booking-embedded-inline" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // The overlay, or nothing when embedded — there the iframe is the frame. A
  // type picked per render rather than a component defined in it: that would be
  // a new type on every render and remount the form under the visitor's cursor.
  const Frame = embedded ? React.Fragment : "div";
  const frameProps = embedded
    ? {}
    : { className: "booking-modal-overlay", onClick: handleClose };

  return (
    <Frame {...frameProps}>
      <div
        className={contentClassName}
        style={accentStyle}
        onClick={embedded ? undefined : (e) => e.stopPropagation()}
      >
        <div className="booking-modal-header">
          <h2>{organizationName || t("Booking.title")}</h2>
          {canClose && (
            <button
              className="booking-modal-close"
              onClick={handleClose}
              aria-label={t("Booking.close")}
            >
              &times;
            </button>
          )}
        </div>

        {confirmed ? (
          <div className="booking-modal-form">
            <div className="booking-confirmed" role="status">
              <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
              <h3>{t("Booking.confirmedTitle")}</h3>
              <p>{t("Booking.confirmedBody")}</p>
              {confirmed.confirmationCode ? (
                <p className="booking-confirmed-code">
                  {t("Booking.confirmationCode")}
                  <strong>{confirmed.confirmationCode}</strong>
                </p>
              ) : null}
            </div>
            <div className="booking-modal-footer">
              <button
                type="button"
                className="btn-confirm"
                onClick={canClose ? handleClose : resetForm}
              >
                {canClose ? t("Booking.done") : t("Booking.bookAnother")}
              </button>
            </div>
          </div>
        ) : (
          <form className="booking-modal-form" onSubmit={handleSubmit}>
            {errorText && (
              <div className="booking-error-message">
                <i className="bi bi-exclamation-circle"></i> {errorText}
              </div>
            )}

            {/* Category Selection - only when this business uses categories */}
            {showCategories && (
              <div className="booking-form-group">
                <label>{t("Booking.selectCategory")}</label>
                <div className="category-pills">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      className={`category-pill ${
                        selectedCategory === category.id ? "selected" : ""
                      }`}
                      onClick={() => handleCategorySelect(category.id)}
                      aria-pressed={selectedCategory === category.id}
                    >
                      {category.name}
                    </button>
                  ))}
                  {hasUncategorized && (
                    <button
                      type="button"
                      className={`category-pill ${
                        selectedCategory === "other" ? "selected" : ""
                      }`}
                      onClick={() => handleCategorySelect("other")}
                      aria-pressed={selectedCategory === "other"}
                    >
                      {t("Booking.otherCategory")}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Service Selection */}
            <div className="booking-form-group">
              <label htmlFor="service">{t("Booking.selectService")}</label>
              <select
                id="service"
                value={selectedService}
                onChange={(e) => {
                  setSelectedService(e.target.value);
                  setSelectedResource(""); // Reset resource when service changes
                  setSelectedDate("");
                  setSelectedTimeSlot("");
                  setDatePageStarts([]);
                }}
                required
                className="booking-select"
                disabled={loading || needsCategory}
              >
                <option value="">
                  {loading
                    ? t("Booking.loadingServices")
                    : needsCategory ? t("Booking.chooseCategoryFirst") : t("Booking.chooseService")}
                </option>
                {visibleServices.map((service) => (
                  <option key={service.id} value={service.id}>
                    {`${service.name} · ${t("Booking.minutes", {
                      count: serviceMinutes(service),
                    })}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Resource Selection - Only show after service is selected */}
            {selectedService && (
              <div className="booking-form-group">
                <label htmlFor="resource">{t("Booking.selectStaff")}</label>
                <select
                  id="resource"
                  value={selectedResource}
                  onChange={(e) => {
                    setSelectedResource(e.target.value);
                    setSelectedDate("");
                    setSelectedTimeSlot("");
                    setDatePageStarts([]);
                  }}
                  required
                  className="booking-select"
                  disabled={loading}
                >
                  <option value="">
                    {loading
                      ? t("Booking.loadingStaff")
                      : t("Booking.chooseStaff")}
                  </option>
                  {offeredResources.map((resource) => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name}
                      {resource.type && resource.type !== "Person"
                        ? ` (${resource.type})`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Date Selection - Only show after resource is selected */}
            {selectedResource && (
              <div className="booking-form-group">
                {/* The arrows sit beside the heading rather than either side
                    of the strip, so the day cards run the full width of the
                    fields above them and still fit a phone screen. */}
                <div className="date-strip-header">
                  <label id={dateLabelId}>{t("Booking.selectDate")}</label>
                  <div className="date-strip-controls">
                    <button
                      ref={calendarToggleRef}
                      type="button"
                      className="date-calendar-toggle"
                      onClick={() => setCalendarOpen((open) => !open)}
                      aria-expanded={calendarOpen}
                      aria-controls={calendarId}
                      aria-label={t("Booking.openCalendar")}
                      title={t("Booking.openCalendar")}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
                        <path d="M3.5 10h17M8 3v4M16 3v4" />
                      </svg>
                      <span className="date-calendar-toggle-text">
                        {t("Booking.calendar")}
                      </span>
                    </button>
                    {/* The strip's own paging; the month view pages by month
                        inside itself. */}
                    {!calendarOpen && (
                      <>
                        <button
                          type="button"
                          className="date-nav-btn"
                          onClick={handlePreviousDates}
                          disabled={datePageStarts.length === 0}
                          aria-label={t("Booking.previousDays")}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M15 5l-7 7 7 7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="date-nav-btn"
                          onClick={handleNextDates}
                          disabled={datesLoading || !hasMoreDates}
                          aria-label={t("Booking.nextDays")}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {calendarOpen ? (
                  <div id={calendarId}>
                    <BookingCalendar
                      organizationSlug={organizationSlug}
                      resourceId={selectedResource}
                      serviceId={selectedService}
                      selectedDate={selectedDate}
                      initialMonth={selectedDate || stripDates[0] || datePageStart}
                      todayValue={todayValue}
                      locale={dateLocale}
                      onSelect={handleCalendarSelect}
                      onDismiss={closeCalendar}
                    />
                  </div>
                ) : datesLoading ? (
                  // Placeholders the size of the cards, so the form does not
                  // jump while a page loads.
                  <div
                    className="date-selector-grid"
                    style={{ "--date-columns": daysPerPage }}
                    aria-busy="true"
                  >
                    <span className="sr-only" role="status">
                      {t("Booking.loadingDates")}
                    </span>
                    {Array.from({ length: daysPerPage }, (_, i) => (
                      <div
                        key={i}
                        className="date-card date-card-skeleton"
                        aria-hidden="true"
                      >
                        <span className="date-card-day">&nbsp;</span>
                        <span className="date-card-number">&nbsp;</span>
                        <span className="date-card-month">&nbsp;</span>
                      </div>
                    ))}
                  </div>
                ) : stripDates.length === 0 ? (
                  <div
                    className="no-slots-message date-strip-message"
                    role="status"
                  >
                    {t("Booking.noDates")}
                  </div>
                ) : (
                  <div
                    className="date-selector-grid"
                    style={{ "--date-columns": daysPerPage }}
                    role="group"
                    aria-labelledby={dateLabelId}
                  >
                    {stripDates.map((dateValue) => {
                      const date = fromISODate(dateValue);
                      const isSelected = selectedDate === dateValue;
                      const isToday = dateValue === todayValue;
                      const fullDate = date.toLocaleDateString(dateLocale, {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      });

                      return (
                        <button
                          key={dateValue}
                          type="button"
                          className={`date-card${isSelected ? " selected" : ""}${
                            isToday ? " today" : ""
                          }`}
                          onClick={() => setSelectedDate(dateValue)}
                          aria-pressed={isSelected}
                          aria-label={
                            isToday ? `${t("Booking.today")}, ${fullDate}` : fullDate
                          }
                        >
                          <span className="date-card-day">
                            {isToday
                              ? t("Booking.today")
                              : datePart(date, { weekday: "short" })}
                          </span>
                          <span className="date-card-number">
                            {date.getDate()}
                          </span>
                          <span className="date-card-month">
                            {datePart(date, { month: "short" })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Time Slot Selection */}
            {selectedDate && (
              <div className="booking-form-group">
                <label>{t("Booking.selectTime")}</label>
                {loading ? (
                  <div className="loading-message">
                    {t("Booking.loadingSlots")}
                  </div>
                ) : timeSlots.length === 0 ? (
                  <div className="no-slots-message">{t("Booking.noSlots")}</div>
                ) : (
                  <div className="time-slots-grid">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        className={`time-slot-btn ${
                          selectedTimeSlot === time ? "selected" : ""
                        }`}
                        onClick={() => setSelectedTimeSlot(time)}
                      >
                        {formatSlotLabel(time)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Customer Information */}
            {selectedTimeSlot && (
              <div className="booking-form-group customer-info">
                <h3>{t("Booking.yourInformation")}</h3>

                {/* Already on file: a summary and a way out of it, rather than
                    three required fields asking for what the account holds. */}
                {!editingDetails && prefillCustomer ? (
                  <div className="booking-identity">
                    <div className="booking-identity-lines">
                      <span className="booking-identity-name">
                        {customerInfo.fullName}
                      </span>
                      <span>{customerInfo.email}</span>
                      {customerInfo.phone ? (
                        <span>{customerInfo.phone}</span>
                      ) : (
                        <span className="booking-identity-missing">
                          {t("Booking.noPhoneOnFile")}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="booking-identity-edit"
                      onClick={() => setEditingDetails(true)}
                    >
                      {t("Booking.changeDetails")}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="form-row">
                      <input
                        type="text"
                        placeholder={t("Booking.fullName")}
                        aria-label={t("Booking.fullName")}
                        value={customerInfo.fullName}
                        onChange={(e) =>
                          handleCustomerInfoChange("fullName", e.target.value)
                        }
                        required
                        className="booking-input"
                      />
                    </div>
                    {/* A pattern on each, because the browser's own check lets
                        "177" through as a phone and "ana@gmail" as an address
                        (see utils/contactFormat). Its title is the sentence
                        the bubble shows — utils/formValidation. */}
                    <div className="form-row">
                      <input
                        type="email"
                        placeholder={t("Booking.email")}
                        aria-label={t("Booking.email")}
                        value={customerInfo.email}
                        onChange={(e) =>
                          handleCustomerInfoChange("email", e.target.value)
                        }
                        required
                        pattern={EMAIL_PATTERN}
                        maxLength={EMAIL_MAX_LENGTH}
                        title={t("Booking.emailHint")}
                        className="booking-input"
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="tel"
                        placeholder={t("Booking.phone")}
                        aria-label={t("Booking.phone")}
                        value={customerInfo.phone}
                        onChange={(e) =>
                          handleCustomerInfoChange("phone", e.target.value)
                        }
                        required
                        pattern={PHONE_PATTERN}
                        maxLength={PHONE_MAX_LENGTH}
                        title={t("Booking.phoneHint")}
                        className="booking-input"
                      />
                    </div>
                  </>
                )}

                <div className="form-row">
                  <textarea
                    placeholder={t("Booking.notes")}
                    aria-label={t("Booking.notes")}
                    rows="3"
                    value={customerInfo.notes}
                    onChange={(e) =>
                      handleCustomerInfoChange("notes", e.target.value)
                    }
                    className="booking-textarea"
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="booking-modal-footer">
              {canClose && (
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  {t("Booking.cancel")}
                </button>
              )}
              <button
                type="submit"
                className="btn-confirm"
                disabled={
                  needsCategory ||
                  !selectedService ||
                  !selectedResource ||
                  !selectedDate ||
                  !selectedTimeSlot ||
                  submitting
                }
              >
                {submitting ? t("Booking.confirming") : t("Booking.confirm")}
              </button>
            </div>
          </form>
        )}
      </div>
    </Frame>
  );
};

BookingModal.propTypes = {
  isOpen: PropTypes.bool,
  /** Omitted only by the inline widget, which has nothing to close. */
  onClose: PropTypes.func,
  organizationSlug: PropTypes.string,
  /** Shown as the modal's heading; falls back to a generic "Book an appointment". */
  organizationName: PropTypes.string,
  initialSelection: PropTypes.shape({
    serviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    timeSlot: PropTypes.string,
  }),
  /**
   * The signed-in account's details. Given, the identity fields start collapsed
   * behind a summary; omitted, the form asks for them as it always has.
   */
  prefillCustomer: PropTypes.shape({
    fullName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
  /** Rendered by the widget: no overlay, the iframe is the frame. */
  embedded: PropTypes.oneOf(["popup", "inline"]),
  /** Sent with the booking so the business can see where it came from ("Widget"). */
  source: PropTypes.string,
  /** The business's colours as --booking-accent* custom properties. */
  accentStyle: PropTypes.object,
  /** Called once a booking has been confirmed. */
  onBooked: PropTypes.func,
};

export default BookingModal;
